import {apiFetch} from '../security/apiFetch';
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';

interface User { id: string; username: string; email: string; role: string; permissions: string[]; isActive: boolean; }
interface AuthContextType {
  user: User | null; token: string | null; isAuthenticated: boolean; loading: boolean; error: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>; refreshAuth: () => Promise<void>;
  hasPermission: (permission: string) => boolean; hasRole: (role: string) => boolean; clearError: () => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
const PERMISSIONS = ['admin:read', 'admin:write', 'monitoring:read', 'users:manage', 'tokens:manage'];
type Session = { token: string; expiresAt: number; user: User };

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const session = useRef<Session | null>(null);
  const generation = useRef(0);
  const clearSession = useCallback(() => {
    generation.current += 1; session.current = null; setToken(null); setUser(null); setLoading(false);
  }, []);

  const refreshAuth = useCallback(async () => {
    const current = session.current;
    if (!current || current.expiresAt <= Date.now()) { clearSession(); return; }
    try {
      const response = await apiFetch('/api/v1/auth/admin/session', { headers: { Authorization: `Bearer ${current.token}` }, cache: 'no-store' });
      const identity = response.ok ? await response.json() : null;
      if (session.current !== current) return;
      if (!identity || identity.id !== current.user.id || identity.role !== 'admin' || !identity.scopes?.includes('admin')) {
        clearSession(); setError('Сессия закрыта или права изменились. Войдите снова.');
      }
      // Checking a session never extends its original expiry.
    } catch {
      if (session.current === current) { clearSession(); setError('Не удалось проверить сессию. Войдите снова.'); }
    }
  }, [clearSession]);

  const login = async (username: string, password: string): Promise<boolean> => {
    clearSession(); const attempt = generation.current; setLoading(true); setError(null);
    try {
      const response = await apiFetch('/api/v1/auth/admin-login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }), cache: 'no-store' });
      if (!response.ok) throw new Error(response.status === 429 ? 'Слишком много попыток. Повторите через пять минут.' : response.status >= 500 ? 'Сервис входа недоступен.' : 'Неверный логин или пароль.');
      const data = await response.json();
      if (!data.access_token || !Number.isInteger(data.expires_in) || data.expires_in <= 0 || data.expires_in > 900 || !data.user?.id) throw new Error('Сервер вернул неверную сессию.');
      const expiresAt = Date.now() + data.expires_in * 1000;
      const check = await apiFetch('/api/v1/auth/admin/session', { headers: { Authorization: `Bearer ${data.access_token}` }, cache: 'no-store' });
      const identity = check.ok ? await check.json() : null;
      if (!identity || identity.id !== data.user.id || identity.role !== 'admin' || !identity.scopes?.includes('admin')) throw new Error('Нет действующих прав Admin.');
      if (attempt !== generation.current || expiresAt <= Date.now()) return false;
      const nextUser: User = { id: identity.id, username: data.user.username || username, email: '', role: identity.role, permissions: PERMISSIONS, isActive: true };
      session.current = { token: data.access_token, expiresAt, user: nextUser };
      setUser(nextUser); setToken(data.access_token); return true;
    } catch (failure) {
      if (attempt === generation.current) setError(failure instanceof Error ? failure.message : 'Ошибка подключения к серверу.');
      return false;
    } finally { if (attempt === generation.current) setLoading(false); }
  };

  const logout = useCallback(async () => {
    const current = session.current; clearSession(); setError(null);
    if (!current) return;
    try {
      const response = await apiFetch('/api/v1/auth/admin/logout', { method: 'POST', headers: { Authorization: `Bearer ${current.token}` }, cache: 'no-store' });
      if (!response.ok && response.status !== 401) throw new Error();
    } catch { setError('Локальный выход выполнен, но сервер не подтвердил отзыв. Сессия истечёт по своему сроку.'); }
  }, [clearSession]);

  useEffect(() => {
    // Remove historical persisted credentials; neither storage nor URL can authenticate.
    try { localStorage.removeItem('spiritual_platform_auth'); } catch { /* Storage can be disabled. */ }
    const url = new URL(window.location.href);
    if (url.searchParams.has('token')) { url.searchParams.delete('token'); window.history.replaceState(null, '', url.pathname + url.search + url.hash); }
    setLoading(false);
  }, []);
  useEffect(() => {
    if (!token || !session.current) return;
    const timeout = window.setTimeout(() => { clearSession(); setError('Срок сессии истёк. Войдите снова.'); }, Math.max(0, session.current.expiresAt - Date.now()));
    const interval = window.setInterval(() => { void refreshAuth(); }, 30000);
    const visible = () => { if (document.visibilityState === 'visible') void refreshAuth(); };
    document.addEventListener('visibilitychange', visible);
    return () => { clearTimeout(timeout); clearInterval(interval); document.removeEventListener('visibilitychange', visible); };
  }, [token, clearSession, refreshAuth]);

  const clearError = useCallback(() => setError(null), []);
  return <AuthContext.Provider value={{ user, token, isAuthenticated: !!user && !!token, loading, error, login, logout, refreshAuth,
    hasPermission: permission => !!user?.permissions.includes(permission), hasRole: role => user?.role === role, clearError }}>{children}</AuthContext.Provider>;
};
